import { Injectable, inject } from '@angular/core';
import { ModalController } from '@ionic/angular';

import { Me } from 'src/providers/me/me';
import { Client } from 'src/providers/client/client';
import { DemoAccountService } from 'src/shared/services/demo-account.service';
import { SubscriptionsQueriesService } from 'src/shared/services/subscriptions-queries.service';
import { Util } from 'src/shared/util/util';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import {
  SubscriptionChangeMode,
  SubscriptionUpgradeModalComponent
} from 'src/shared/components/subscription-upgrade-modal/subscription-upgrade-modal.component';
import {
  CLEANING_MAINTENANCE_PER_UNIT,
  RESERVATION_FEE_PERCENT
} from 'src/shared/components/subscription-plan-card/subscription-plan-card.component';

export const CLEANING_MAINTENANCE_PLAN_KEY = 'full-autopilot-cleaning-maintenance';
// Every paid rental plan — `full-autopilot`, `full-autopilot-cleaning-maintenance` and its
// discounted `-35` … `-0` variants — shares this prefix (billing_api#906).
export const FULL_AUTOPILOT_PLAN_PREFIX = 'full-autopilot';
/** Full Autopilot without the Cleaning & Maintenance add-on — the target when removing it. */
export const FULL_AUTOPILOT_PLAN_KEY = 'full-autopilot';
/**
 * The signup default the app treats as "you don't have a subscription" (see subscription.html) —
 * gotidy's customer create service assigns it to every brand new account. There is no separate
 * cancel endpoint; canceling would mean moving back to this plan, which is why the subscription
 * page routes cancellations to the concierge instead of doing it itself.
 */
export const NO_SUBSCRIPTION_PLAN_KEY = 'autopilot-us';

/** Plan-specific numbers for the prompt's line-item summary; each falls back to the list price. */
export interface SubscriptionChangeDetails {
  /** $/unit/month the account actually pays for the Cleaning & Maintenance add-on. */
  perUnitDollars?: number;
  /** % of gross bookings the plan charges at guest checkout. */
  feePercent?: number;
}

/** True when any of the client's active subscriptions is one of the Full Autopilot plans. */
export function hasFullAutopilotPlan(subscriptions: any[] | null | undefined): boolean {
  return (subscriptions || []).some((subscription) =>
    String(subscription?.plan?.key || '').startsWith(FULL_AUTOPILOT_PLAN_PREFIX)
  );
}

/**
 * Gates cleaning & maintenance features for rental clients whose subscription
 * doesn't include TIDY managing cleaning: no subscription at all (including the
 * `autopilot-us` signup default the app treats as "no subscription") or a
 * `full-autopilot` plan WITHOUT the `-cleaning-maintenance` add-on. Booking a job,
 * adding/opening pros, adding restocking items, and adding maintenance tasks all
 * coordinate pros through TIDY, which only the cleaning & maintenance plans
 * include (Trello 18574).
 *
 * Usage mirrors DemoBlockService.block() / FeatureGateService.gate() — gate FIRST,
 * abort if it returns true:
 *
 *   async addJob() {
 *     if (await this.subscriptionGate.gate()) { return; }
 *     ...the real action...
 *   }
 *
 * Only fires for a real (non-demo) RENTAL account. Demo sessions keep their own
 * DemoBlockService prompts, non-rental accounts and rentals on legacy paid plans
 * are never gated, and any error reading the subscription fails open so the gate
 * can never brick a paying user. The prompt shows the new monthly total. For Full
 * Autopilot without the add-on, its CTA upgrades the subscription to
 * `full-autopilot-cleaning-maintenance` right from the modal. For a client with no
 * plan, its CTA opens the Subscribe page instead, which takes a card and the amount
 * due today before subscribing (Trello 18953).
 *
 * The same modal backs the subscription page's Cleaning & Maintenance toggle, via
 * promptUpgrade / promptRemoveCleaningMaintenance.
 */
@Injectable({ providedIn: 'root' })
export class SubscriptionGateService {

  private client = inject(Client);
  private demoAccountService = inject(DemoAccountService);
  private me = inject(Me);
  private modalController = inject(ModalController);
  private subscriptionsQueries = inject(SubscriptionsQueriesService);
  private util = inject(Util);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);

  private isPromptOpen = false;

  /** Shows the "upgrade your subscription" prompt and returns true so the caller aborts. */
  async gate(): Promise<boolean> {
    const mode = await this.gateMode();
    if (!mode) {
      return false;
    }
    void this.showPrompt(mode);
    return true;
  }

  /**
   * Opens the upgrade prompt directly, without the rental/plan checks — for explicit
   * upsell entry points like the subscription page's Cleaning & Maintenance toggle.
   * Resolves true only once the plan change has actually gone through.
   */
  promptUpgrade(details?: SubscriptionChangeDetails): Promise<boolean> {
    return this.showPrompt('add', details);
  }

  /** Drops the Cleaning & Maintenance add-on, keeping Full Autopilot. */
  promptRemoveCleaningMaintenance(details?: SubscriptionChangeDetails): Promise<boolean> {
    return this.showPrompt('remove', details);
  }

  /**
   * Whether the account already holds a Full Autopilot subscription (any `full-autopilot*`
   * plan, current or activating next cycle). Always reads the API — never the query cache —
   * because the answer decides whether the subscribe page may take a payment at all: client
   * 695174 was charged three times in Sept 2026 when the subscribe link/page reopened against
   * a stale "not subscribed" cache. Demo sessions are never subscribed; a failed read returns
   * false so the subscribe page keeps working exactly as before.
   */
  async hasFullAutopilotSubscription(): Promise<boolean> {
    if (this.demoAccountService.isDemoSession()) {
      return false;
    }
    try {
      const subscriptions = await this.subscriptionsQueries.getFreshSubscriptions();
      return hasFullAutopilotPlan(subscriptions);
    } catch (_err) {
      return false;
    }
  }

  /**
   * Which prompt a gated control shows, or null when it isn't gated: 'subscribe' for a
   * client with no plan (the Subscribe page takes the card and the first payment), 'add'
   * for Full Autopilot without the Cleaning & Maintenance add-on.
   */
  private async gateMode(): Promise<'subscribe' | 'add' | null> {
    if (this.demoAccountService.isDemoSession()) {
      return null;
    }
    if (localStorage.getItem('isRentalClient') !== 'true') {
      return null;
    }
    try {
      const subscriptions = await this.subscriptionsQueries.getSubscriptions();
      const currentSubscription = this.me.getCurrentSubscription(subscriptions || []);
      const planKey = currentSubscription?.plan?.key || '';
      // No subscription at all — `autopilot-us` is the signup default the app
      // treats as "you don't have a subscription" (see subscription.html).
      if (!planKey || planKey === NO_SUBSCRIPTION_PLAN_KEY) {
        return 'subscribe';
      }
      return planKey.startsWith(FULL_AUTOPILOT_PLAN_PREFIX) && !planKey.includes('cleaning-maintenance') ? 'add' : null;
    } catch (_err) {
      return null;
    }
  }

  /** Resolves true when the client confirmed AND the plan change succeeded. */
  private async showPrompt(mode: SubscriptionChangeMode, details: SubscriptionChangeDetails = {}): Promise<boolean> {
    // Guard against double-taps and against two gated controls firing at once.
    if (this.isPromptOpen) {
      return false;
    }
    this.isPromptOpen = true;
    // Unit count drives the modal's monthly-total summary; an account with no
    // addresses yet still quotes 1 unit, matching the subscribe page.
    let unitCount = 1;
    try {
      const addresses = await this.client.getAddresses();
      unitCount = Math.max(addresses?.length || 0, 1);
    } catch (_err) {
      // Keep the 1-unit default.
    }
    const modal = await this.modalController.create({
      component: SubscriptionUpgradeModalComponent,
      componentProps: {
        mode,
        unitCount,
        perUnitDollars: details.perUnitDollars ?? CLEANING_MAINTENANCE_PER_UNIT,
        feePercent: details.feePercent ?? RESERVATION_FEE_PERCENT
      },
      cssClass: 'demo-block-modal-wrapper',
      backdropDismiss: true
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    this.isPromptOpen = false;
    if (!data?.confirm) {
      return false;
    }
    if (mode === 'subscribe') {
      await this.openSubscribePage();
      return false;
    }
    return this.changePlan(mode);
  }

  /**
   * The Subscribe page (`go-live/enable-autopilot`) opens with Cleaning & Maintenance on and
   * only subscribes once a card is on file and the amount due today is paid. Leftover
   * getting-started steps and flags are cleared, as the subscribe redirect does, so
   * subscribing just closes the page.
   */
  private async openSubscribePage(): Promise<void> {
    await this.storage.delete('autopilot-pending-step');
    await this.storage.delete('autopilot-pending-action');
    this.rightSidePanelService.setDialogPageTitle('Subscribe');
    await this.rightSidePanelService.navigateTo('go-live/enable-autopilot', { fromGettingStarted: false, fromSubscribeStep: false });
  }

  private async changePlan(mode: SubscriptionChangeMode): Promise<boolean> {
    const { planKey, successMessage } = this.planChangeFor(mode);
    try {
      await this.me.changeSubscription({ plan_type_key: planKey });
      this.subscriptionsQueries.invalidate();
      this.util.showSuccess(successMessage);
      return true;
    } catch (error: any) {
      this.util.showError(error?.error?.message || error?.message || 'Unable to update your subscription. Please contact your account manager.');
      return false;
    }
  }

  private planChangeFor(mode: SubscriptionChangeMode): { planKey: string, successMessage: string } {
    if (mode === 'remove') {
      return {
        planKey: FULL_AUTOPILOT_PLAN_KEY,
        successMessage: 'Cleaning & Maintenance removed from your subscription.'
      };
    }
    return {
      planKey: CLEANING_MAINTENANCE_PLAN_KEY,
      successMessage: 'Cleaning & Maintenance added to your subscription!'
    };
  }
}
